import React, {
  createContext,
  RefObject,
  useCallback,
  useContext,
  useRef,
  useState,
  ReactNode,
} from "react";

interface OnboardingSidebarContextType {
  sidebarContent: ReactNode;
  sidebarKey: string;
  headerAction: ReactNode;
  setSidebarContent: (content: ReactNode, key: string) => void;
  setHeaderAction: (action: ReactNode) => void;
  scrollContainerRef: RefObject<HTMLElement>;
  scrollToTop: () => void;
}

const OnboardingSidebarContext = createContext<OnboardingSidebarContextType | null>(null);

export const OnboardingSidebarProvider = ({
  children,
  sidebarContent,
  sidebarKey,
}: {
  children: ReactNode;
  sidebarContent: ReactNode;
  sidebarKey: string;
}) => {
  const [sidebarContentState, setSidebarContentState] = useState<ReactNode>(sidebarContent);
  const [sidebarKeyState, setSidebarKeyState] = useState<string>(sidebarKey || "default");
  const [headerAction, setHeaderAction] = useState<ReactNode>(null);
  const scrollContainerRef = useRef<HTMLElement>(null);

  const setSidebarContent = (content: ReactNode, key: string) => {
    setSidebarContentState(content);
    setSidebarKeyState(key);
  };

  const scrollToTop = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <OnboardingSidebarContext.Provider
      value={{
        sidebarContent: sidebarContentState,
        sidebarKey: sidebarKeyState,
        headerAction,
        setSidebarContent,
        setHeaderAction,
        scrollContainerRef,
        scrollToTop,
      }}
    >
      {children}
    </OnboardingSidebarContext.Provider>
  );
};

export const useOnboardingSidebar = () => {
  const context = useContext(OnboardingSidebarContext);
  if (!context) {
    throw new Error("useOnboardingSidebar must be used within an OnboardingSidebarProvider");
  }
  return context;
};
